import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import {
  MessageCircle,
  CheckCircle2,
  XCircle,
  Copy,
  RefreshCw,
  Unlink,
  Smartphone,
  Mic,
  BarChart3,
  HelpCircle,
  AlertCircle,
  ExternalLink,
  Bell,
  BellOff,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SANDBOX_JOIN_CODE = "join ago-family";

export default function WhatsAppSettings() {
  const [generatedCode, setGeneratedCode] = useState<{
    code: string;
    expiresAt: number;
    sandboxNumber: string | null;
    mode: "sandbox" | "production" | "unconfigured";
    message: string;
  } | null>(null);

  const { data: status, isLoading, refetch } = trpc.whatsapp.getStatus.useQuery();

  const generateCode = trpc.whatsapp.generateCode.useMutation({
    onSuccess: (data) => {
      setGeneratedCode(data);
    },
    onError: (err) => {
      toast.error("Erro ao gerar código: " + err.message);
    },
  });

  const [reminderHour, setReminderHour] = useState<number>(7);

  const setReminder = trpc.whatsapp.setReminder.useMutation({
    onSuccess: (data) => {
      if (data.enabled) {
        toast.success("Lembrete matinal ativado!");
      } else {
        toast.success("Lembrete matinal desativado.");
      }
      refetch();
    },
    onError: (err) => {
      toast.error("Erro ao configurar lembrete: " + err.message);
    },
  });

  const unlink = trpc.whatsapp.unlink.useMutation({
    onSuccess: () => {
      toast.success("Número desvinculado com sucesso");
      setGeneratedCode(null);
      refetch();
    },
    onError: (err) => {
      toast.error("Erro ao desvincular: " + err.message);
    },
  });

  function copyToClipboard(text: string, label = "Copiado!") {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(label);
    });
  }

  // Sincroniza hora do lembrete quando status carrega
  const reminderHourValue = status?.reminderHour ?? 7;
  const isSandbox = status?.mode === "sandbox";
  const whatsappAvailable = status?.mode !== "unconfigured";

  function formatPhone(phone: string) {
    if (phone.length === 13) {
      return `+${phone.slice(0, 2)} (${phone.slice(2, 4)}) ${phone.slice(4, 9)}-${phone.slice(9)}`;
    }
    return `+${phone}`;
  }

  function timeLeft(expiresAt: number) {
    const diff = Math.max(0, expiresAt - Date.now());
    const mins = Math.floor(diff / 60000);
    return mins > 0 ? `${mins} min` : "Expirado";
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <MessageCircle className="h-6 w-6 text-green-600" />
          WhatsApp
        </h1>
        <p className="text-muted-foreground mt-1">
          Registre lançamentos financeiros enviando mensagens de texto ou áudio pelo WhatsApp.
        </p>
      </div>

      {/* Status atual */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Status da vinculação</CardTitle>
        </CardHeader>
        <CardContent>
          {status?.linked ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium">Número vinculado</p>
                  <p className="text-sm text-muted-foreground">{formatPhone(status.phoneNumber ?? "")}</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => unlink.mutate()}
                disabled={unlink.isPending}
                className="text-destructive hover:text-destructive"
              >
                {unlink.isPending ? <Spinner className="h-4 w-4" /> : <Unlink className="h-4 w-4 mr-1" />}
                Desvincular
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <XCircle className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Nenhum número vinculado</p>
                <p className="text-sm text-muted-foreground">Siga os passos abaixo para conectar seu WhatsApp</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vincular número */}
      {!status?.linked && !whatsappAvailable && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">WhatsApp em preparação</CardTitle>
            <CardDescription>
              O registro pelo WhatsApp será liberado quando o número oficial de produção estiver configurado.
              Enquanto isso, você pode registrar lançamentos pelo aplicativo, por voz ou com foto de comprovante.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {!status?.linked && whatsappAvailable && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Como vincular seu WhatsApp</CardTitle>
            <CardDescription>
              {isSandbox ? "Siga os 4 passos abaixo. Leva menos de 2 minutos." : "Siga os 2 passos abaixo. Leva menos de 2 minutos."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">

            {isSandbox && (
              <div className="flex gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  Você está no ambiente de testes do WhatsApp. Esta etapa extra não aparecerá quando o número oficial estiver liberado.
                </p>
              </div>
            )}

            {/* Passo 1 — salvar número */}
            {isSandbox && <div className="flex gap-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0 mt-0.5">1</div>
              <div className="flex-1">
                <p className="text-sm font-medium">Salve este número no seu celular</p>
                <p className="text-xs text-muted-foreground mt-0.5">Salve como "Bolso Finanças" ou qualquer nome que preferir</p>
                <div className="flex items-center gap-2 mt-2">
                  <code className="bg-muted px-3 py-1.5 rounded text-sm font-mono flex-1 select-all">
                    {status?.sandboxNumber ?? "+1 415 523 8886"}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(status?.sandboxNumber ?? "", "Número copiado!")}
                    aria-label="Copiar número do WhatsApp"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>}

            {isSandbox && <Separator />}

            {/* Passo 2 — join sandbox */}
            {isSandbox && <div className="flex gap-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0 mt-0.5">2</div>
              <div className="flex-1">
                <p className="text-sm font-medium">Ative o número enviando esta mensagem</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Abra o WhatsApp, encontre o contato que você acabou de salvar e envie exatamente:
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <code className="bg-muted px-3 py-1.5 rounded text-sm font-mono flex-1 select-all">
                    {SANDBOX_JOIN_CODE}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(SANDBOX_JOIN_CODE, "Mensagem copiada!")}
                    aria-label="Copiar mensagem de ativação"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Você receberá uma mensagem de confirmação do Twilio. Isso é feito <strong>uma única vez</strong> por número.
                </p>
              </div>
            </div>}

            {isSandbox && <Separator />}

            {/* Passo 3 — gerar código */}
            <div className="flex gap-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0 mt-0.5">{isSandbox ? 3 : 1}</div>
              <div className="flex-1">
                <p className="text-sm font-medium">Gere seu código de vinculação</p>
                <p className="text-xs text-muted-foreground mt-0.5">Clique no botão abaixo para gerar um código único e temporário</p>
                {!generatedCode ? (
                  <Button
                    onClick={() => generateCode.mutate()}
                    disabled={generateCode.isPending}
                    size="sm"
                    className="mt-2"
                  >
                    {generateCode.isPending ? (
                      <Spinner className="h-4 w-4 mr-2" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Gerar código
                  </Button>
                ) : (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <code className="bg-muted px-3 py-1.5 rounded text-sm font-mono flex-1 select-all">
                        {generatedCode.message}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(generatedCode.message, "Código copiado!")}
                        aria-label="Copiar código de vinculação"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Válido por {timeLeft(generatedCode.expiresAt)} •{" "}
                      <button
                        onClick={() => generateCode.mutate()}
                        className="underline underline-offset-2 hover:text-foreground transition-colors"
                      >
                        Gerar novo código
                      </button>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {generatedCode && (
              <>
                <Separator />

                {/* Passo 4 — enviar código */}
                <div className="flex gap-3">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0 mt-0.5">{isSandbox ? 4 : 2}</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Envie o código no WhatsApp</p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Envie a mensagem acima para o número oficial do Bolso. Você receberá uma confirmação automática.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => refetch()}
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Já enviei — verificar vinculação
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Como usar */}
      {/* Card: Lembrete Matinal */}
      {status?.linked && status.mode === "production" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Lembrete Matinal
            </CardTitle>
            <CardDescription>
              Receba um resumo diário do seu saldo e contas do dia via WhatsApp.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="daily-reminder" className="text-sm font-medium">Ativar lembrete diário</Label>
                <p className="text-xs text-muted-foreground">
                  {status.reminderEnabled
                    ? `Ativo — enviado às ${status.reminderHour}h (horário de Brasília)`
                    : "Desativado"}
                </p>
              </div>
              <Switch
                id="daily-reminder"
                checked={status.reminderEnabled}
                onCheckedChange={(checked) => {
                  setReminder.mutate({ enabled: checked, hour: reminderHourValue });
                }}
                disabled={setReminder.isPending}
              />
            </div>

            {status.reminderEnabled && (
              <div className="flex items-center gap-3 pt-1">
                <Label htmlFor="reminder-hour" className="text-sm shrink-0">Horário:</Label>
                <Select
                  value={String(reminderHourValue)}
                  onValueChange={(v) => {
                    const h = parseInt(v, 10);
                    setReminderHour(h);
                    setReminder.mutate({ enabled: true, hour: h });
                  }}
                >
                  <SelectTrigger id="reminder-hour" className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {String(i).padStart(2, "0")}:00
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground space-y-1">
              <p className="font-medium text-foreground">O lembrete inclui:</p>
              <p>• Saldo do mês (receitas, despesas e saldo)</p>
              <p>• Contas vencendo hoje</p>
            </div>
          </CardContent>
        </Card>
      )}

      {status?.linked && status.mode !== "production" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Lembretes automáticos</CardTitle>
            <CardDescription>
              Este recurso será disponibilizado junto com o número oficial do WhatsApp. Durante os testes, use o aplicativo para consultar seus lançamentos.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <HelpCircle className="h-4 w-4" />
            Como usar após vincular
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3">
            <div className="flex gap-3 items-start">
              <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-lg shrink-0">
                <Mic className="h-4 w-4 text-green-700 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium">Áudio ou texto</p>
                <p className="text-sm text-muted-foreground">
                  Envie um áudio ou texto descrevendo o gasto. A IA interpreta automaticamente.
                </p>
                <div className="mt-1 space-y-0.5">
                  <p className="text-xs text-muted-foreground italic">• "almoço oitenta e três reais"</p>
                  <p className="text-xs text-muted-foreground italic">• "gasolina 150"</p>
                  <p className="text-xs text-muted-foreground italic">• "recebi salário 3500"</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 items-start">
              <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg shrink-0">
                <BarChart3 className="h-4 w-4 text-blue-700 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium">Consultas rápidas</p>
                <p className="text-sm text-muted-foreground">
                  Envie comandos para consultar seu saldo e gastos.
                </p>
                <div className="mt-1 space-y-0.5">
                  <p className="text-xs text-muted-foreground italic">• <strong>saldo</strong> → saldo do mês</p>
                  <p className="text-xs text-muted-foreground italic">• <strong>resumo</strong> → top gastos do mês</p>
                  <p className="text-xs text-muted-foreground italic">• <strong>últimos</strong> → 5 lançamentos recentes</p>
                  <p className="text-xs text-muted-foreground italic">• <strong>ajuda</strong> → lista de comandos</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 items-start">
              <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg shrink-0">
                <Smartphone className="h-4 w-4 text-purple-700 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium">Sem abrir o app</p>
                <p className="text-sm text-muted-foreground">
                  Registre lançamentos direto do WhatsApp, sem precisar abrir o Bolso. Tudo sincroniza automaticamente.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
